import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@heroui/react';
import { StatusChip } from '@/components/common/StatusChip';
import { useApplyZoneSync, useZoneSyncPreview } from '@/features/domains/hooks';
import type { SyncItem } from '@/features/domains/types';
import { ExclamationTriangleIcon } from '@/icons';

const short = (v: string) => (v.length > 64 ? `${v.slice(0, 36)}…${v.slice(-16)}` : v);

function ItemRows({ items, showReason }: { items: SyncItem[]; showReason?: boolean }) {
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {items.map((i) => (
        <li key={`${i.action}:${i.origin}:${i.type}:${i.name}:${i.content}`} className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-14 font-mono text-xs font-semibold text-fg">{i.type}</span>
            <span className="font-mono text-xs text-fg">{i.name}</span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-fg-muted" title={i.content}>
              {i.priority != null ? `${i.priority} ` : ''}{short(i.content)}
            </span>
            <StatusChip tone={i.origin === 'generated' ? 'info' : 'neutral'}>
              {i.origin === 'generated' ? 'Nifty record' : 'From current DNS host'}
            </StatusChip>
          </div>
          {showReason && i.reason && <p className="mt-1 text-xs text-fg-muted">{i.reason}</p>}
          {i.warning && (
            <p className="mt-1 flex items-start gap-1 text-xs text-warning">
              <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />{i.warning}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Review-then-apply for managed-zone synchronisation. The preview is read-only; applying
 * only ADDS the listed records as drafts (nothing existing is edited or deleted) and
 * nothing changes publicly until the zone is published.
 */
export function ZoneSyncModal({ zoneId, isOpen, onClose }: { zoneId: string; isOpen: boolean; onClose: () => void }) {
  const preview = useZoneSyncPreview(zoneId, isOpen);
  const apply = useApplyZoneSync(zoneId);
  const report = preview.data;
  const by = (a: SyncItem['action']) => report?.items.filter((i) => i.action === a) ?? [];
  const adds = by('add');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-fg">Synchronise DNS records</ModalHeader>
        <ModalBody>
          {preview.isLoading && <div className="flex justify-center py-10"><Spinner label="Comparing the zone with the domain's records…" /></div>}
          {preview.isError && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger" role="alert">
              The comparison could not be completed. Nothing was changed.
              <Button className="ml-3" size="sm" variant="flat" onPress={() => preview.refetch()}>Retry</Button>
            </div>
          )}
          {report && (
            <div className="space-y-5">
              <p className="text-sm text-fg-muted">
                Adds this domain's verified Nifty mail records and, while DNS is hosted elsewhere, the records the current
                DNS host publishes. Existing records are never changed or removed; added records stay drafts until you publish.
              </p>
              {report.external.used && <p className="rounded-lg border border-border bg-surface-raised/40 p-3 text-xs text-fg-muted">{report.external.note}</p>}

              <section>
                <h4 className="mb-2 text-sm font-semibold text-fg">Will be added ({adds.length})</h4>
                {adds.length ? <ItemRows items={adds} /> : <p className="text-sm text-fg-muted">Nothing to add — the zone already has every record.</p>}
              </section>
              {by('conflict').length > 0 && (
                <section>
                  <h4 className="mb-2 text-sm font-semibold text-warning">Conflicts — left unchanged ({by('conflict').length})</h4>
                  <ItemRows items={by('conflict')} showReason />
                </section>
              )}
              {by('skipped').length > 0 && (
                <section>
                  <h4 className="mb-2 text-sm font-semibold text-fg">Skipped ({by('skipped').length})</h4>
                  <ItemRows items={by('skipped')} showReason />
                </section>
              )}
              <p className="text-xs text-fg-muted">Already in the zone: {report.summary.present} record{report.summary.present === 1 ? '' : 's'}.</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>Cancel</Button>
          <Button color="primary" isDisabled={!report || adds.length === 0} isLoading={apply.isPending}
            onPress={() => apply.mutate(undefined, { onSuccess: onClose })}>
            {adds.length ? `Add ${adds.length} record${adds.length === 1 ? '' : 's'} as drafts` : 'Nothing to add'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
